import { useTranslation } from 'react-i18next';
import { MessagesSquareIcon, PlusIcon } from 'lucide-react';
import dayjs from 'dayjs';
import { Badge, Button, EmptyState, SearchField, Skeleton } from '@noor/ui';
import { MERCHANT_CHAT_STATUS, type MerchantChat } from '@/entities/merchant-chat';

/**
 * The conversation list — title + new-chat button, username search, and the
 * infinite row list with skeleton and empty states.
 */
export function MerchantConversationList({
  chats,
  search,
  onSearchChange,
  isLoading,
  hasNextPage,
  onLoadMore,
  onNewChat,
  onSelect,
}: {
  chats: MerchantChat[];
  search: string;
  onSearchChange: (value: string) => void;
  isLoading: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  onNewChat: () => void;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between p-4">
        <h2 className="text-2xl leading-7 font-semibold tracking-tight text-foreground">
          {t('chat.title')}
        </h2>
        <Button variant="link" onClick={onNewChat} className="gap-1">
          <PlusIcon className="size-4" />
          {t('chat.new_chat')}
        </Button>
      </div>
      <div className="border-y border-border px-4 py-3">
        <SearchField
          id="merchant-chat-search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          onClear={() => onSearchChange('')}
          placeholder={t('chat.search_placeholder')}
          aria-label={t('chat.search_placeholder')}
        />
      </div>
      <div
        className="min-h-0 flex-1 overflow-y-auto"
        onScroll={(event) => {
          // Bottom reached and more pages left: append, like the old loader.
          const target = event.currentTarget;
          if (
            hasNextPage &&
            !isLoading &&
            target.scrollTop + target.clientHeight >= target.scrollHeight - 1
          ) {
            onLoadMore();
          }
        }}
      >
        {chats.length > 0 ? (
          chats.map((chat) => (
            <MerchantConversationRow key={chat.id} chat={chat} onSelect={onSelect} />
          ))
        ) : isLoading ? (
          <div className="flex items-center gap-3 px-6 py-3">
            <Skeleton className="size-12 rounded-full" />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-3 w-1/4" />
            </div>
          </div>
        ) : (
          <div className="flex h-[70%] items-center justify-center p-6">
            <EmptyState
              icon={MessagesSquareIcon}
              title={t('chat.empty_chat_list')}
              description={t('chat.empty_chat_create')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** One row: peer avatar, name, last-message preview, timestamp. */
function MerchantConversationRow({
  chat,
  onSelect,
}: {
  chat: MerchantChat;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation('common');
  const firstName = chat.user?.first_name ?? 'New';
  const lastName = chat.user?.last_name ?? 'Chat';

  return (
    <button
      type="button"
      onClick={() => onSelect(chat.id)}
      className="flex w-full cursor-pointer items-center justify-between gap-3 border-b border-border px-4 py-3 text-left transition-colors outline-none hover:bg-muted focus-visible:bg-muted"
    >
      <span className="flex min-w-0 items-center gap-3">
        <span
          aria-hidden="true"
          className="flex size-12 shrink-0 items-center justify-center rounded-[20px] bg-primary text-base font-medium text-primary-foreground"
        >
          {firstName.charAt(0)}
          {lastName.charAt(0)}
        </span>
        <span className="flex max-w-[220px] min-w-0 flex-col gap-1">
          <span className="truncate text-base leading-[19px] font-semibold tracking-tight text-foreground">
            {firstName} {lastName}
          </span>
          <span className="truncate text-base leading-[19px] font-normal tracking-tight text-muted-foreground">
            {chat.last_message?.message ?? chat.subject ?? ''}
          </span>
        </span>
        {chat.status === MERCHANT_CHAT_STATUS.closedResolved && (
          <Badge variant="secondary" className="mb-4 shrink-0">
            {t('chat.completed')}
          </Badge>
        )}
      </span>
      <span className="shrink-0 text-xs leading-4 font-semibold text-muted-foreground">
        {dayjs(chat.updated_at).isValid()
          ? dayjs(chat.updated_at).format('DD-MM-YYYY HH:mm')
          : ''}
      </span>
    </button>
  );
}
